import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);

      // Ensure profile exists for current user
      if (s?.user) {
        const name = s.user.user_metadata?.full_name || s.user.email?.split('@')[0] || '';
        supabase.from('profiles').upsert(
          { id: s.user.id, full_name: name, updated_at: new Date().toISOString() },
          { onConflict: 'id', ignoreDuplicates: false }
        ).catch(() => {});
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);

      // Ensure profile exists on every sign-in
      if (s?.user) {
        const name = s.user.user_metadata?.full_name || s.user.email?.split('@')[0] || '';
        supabase.from('profiles').upsert(
          { id: s.user.id, full_name: name, updated_at: new Date().toISOString() },
          { onConflict: 'id', ignoreDuplicates: false }
        ).then(({ error }) => {
          if (error) console.error('Profile upsert failed:', error);
        });
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = useCallback(async (email, password) => {
    if (!supabase) throw new Error('Database not connected');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }, []);

  const signUp = useCallback(async (email, password, fullName) => {
    if (!supabase) throw new Error('Database not connected');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName || '' } },
    });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
